import { ReactNode, useRef, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../utils/api'
import { Upload, RefreshCw } from 'lucide-react'
import SignaturePad, { SignaturePadHandle } from './SignaturePad'

export interface WorkOrderSignatureData {
  id: string; signatureUrl: string; signedByName: string | null; source: string; signedAt: string; role: 'STAFF' | 'CUSTOMER'
}

export default function WorkOrderSignatureBox({
  workOrderId, role, label, signature, canManage, isLocked, fmtDateTime,
}: {
  workOrderId: string
  role: 'STAFF' | 'CUSTOMER'
  label: ReactNode
  signature: WorkOrderSignatureData | null
  canManage: boolean
  isLocked: boolean
  fmtDateTime: (d: string | null) => string
}) {
  const qc = useQueryClient()
  const [signedByName, setSignedByName] = useState('')
  const [signing, setSigning] = useState(false)
  const [signMode, setSignMode] = useState<'ONLINE' | 'OFFLINE_SCAN'>('ONLINE')
  const padRef = useRef<SignaturePadHandle>(null)
  const scanRef = useRef<HTMLInputElement>(null)

  const deleteMut = useMutation({
    mutationFn: () => api.delete(`/workorders/${workOrderId}/signature`, { params: { role } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['workorder', workOrderId] }),
  })

  const uploadSignatureFile = async (file: Blob, source: 'ONLINE' | 'OFFLINE_SCAN') => {
    setSigning(true)
    try {
      const fd = new FormData()
      fd.append('file', file, source === 'ONLINE' ? 'signature.png' : (file as File).name || 'signature.jpg')
      fd.append('source', source)
      fd.append('role', role)
      if (signedByName) fd.append('signedByName', signedByName)
      await api.post(`/workorders/${workOrderId}/signature`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      qc.invalidateQueries({ queryKey: ['workorder', workOrderId] })
    } catch (e: any) {
      alert(e.response?.data?.message || 'บันทึกลายเซ็นไม่สำเร็จ')
    } finally { setSigning(false) }
  }

  const saveSignature = async () => {
    if (!padRef.current || padRef.current.isEmpty()) { alert('กรุณาเซ็นชื่อในช่องก่อนบันทึก'); return }
    const blob = await padRef.current.toBlob()
    if (!blob) return
    await uploadSignatureFile(blob, 'ONLINE')
  }

  const uploadScannedSignature = async (files: FileList | null) => {
    const file = files?.[0]
    if (!file) return
    await uploadSignatureFile(file, 'OFFLINE_SCAN')
  }

  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
      <h2 className="font-semibold text-gray-900 flex items-center gap-2 mb-3">{label}</h2>

      {signature ? (
        <div className="space-y-3">
          <img src={signature.signatureUrl} alt="signature" className="border border-gray-200 rounded-xl bg-white max-h-40 object-contain"/>
          <p className="text-sm text-gray-600">
            เซ็นโดย {signature.signedByName || '—'} · {fmtDateTime(signature.signedAt)}
            <span className="ml-2 text-xs text-gray-400">({signature.source === 'OFFLINE_SCAN' ? 'สแกนจากกระดาษ' : 'เซ็นออนไลน์'})</span>
          </p>
          {canManage && (
            <button onClick={() => { if (confirm('ยืนยันลบลายเซ็นนี้?')) deleteMut.mutate() }} className="text-xs text-red-500 hover:underline">ลบลายเซ็น</button>
          )}
        </div>
      ) : isLocked ? (
        <p className="text-sm text-gray-400">ยังไม่มีลายเซ็น</p>
      ) : (
        <div className="space-y-3">
          <div className="flex bg-gray-100 rounded-lg p-1 w-fit">
            <button onClick={() => setSignMode('ONLINE')} className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${signMode === 'ONLINE' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500'}`}>เซ็นออนไลน์</button>
            <button onClick={() => setSignMode('OFFLINE_SCAN')} className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${signMode === 'OFFLINE_SCAN' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500'}`}>อัปโหลดรูปจากกระดาษ</button>
          </div>

          <input value={signedByName} onChange={e => setSignedByName(e.target.value)} placeholder="ชื่อผู้ลงนาม"
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"/>

          {signMode === 'ONLINE' ? (
            <>
              <p className="text-xs text-gray-400">
                {role === 'CUSTOMER' ? 'ให้ลูกค้าเซ็นชื่อในช่องด้านล่างเพื่อยืนยันรับงาน' : 'ให้ผู้ปฏิบัติงานเซ็นชื่อในช่องด้านล่างเพื่อยืนยันการทำงาน'}
              </p>
              <SignaturePad ref={padRef} height={180}/>
              <div className="flex items-center justify-end gap-2">
                <button onClick={() => padRef.current?.clear()} className="px-3 py-2 text-sm text-gray-500 hover:bg-gray-100 rounded-lg">ล้าง</button>
                <button onClick={saveSignature} disabled={signing} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
                  {signing ? 'กำลังบันทึก...' : 'บันทึกลายเซ็น'}
                </button>
              </div>
            </>
          ) : (
            <>
              <p className="text-xs text-gray-400">
                {role === 'CUSTOMER'
                  ? 'สำหรับกรณีพิมพ์ใบงานออกไปให้ลูกค้าเซ็นด้วยมือ — ถ่ายรูปหรือสแกนช่องลายเซ็นที่เซ็นแล้ว แล้วอัปโหลดกลับเข้าระบบที่นี่'
                  : 'สำหรับกรณีพิมพ์ใบงานออกไปให้ผู้ปฏิบัติงานเซ็นด้วยมือ — ถ่ายรูปหรือสแกนช่องลายเซ็นที่เซ็นแล้ว แล้วอัปโหลดกลับเข้าระบบที่นี่'}
              </p>
              <button onClick={() => scanRef.current?.click()} disabled={signing}
                className="w-full flex items-center justify-center gap-2 border-2 border-dashed border-gray-300 rounded-xl py-6 text-sm text-gray-500 hover:border-blue-300 hover:bg-blue-50 disabled:opacity-50">
                {signing ? <RefreshCw size={16} className="animate-spin"/> : <Upload size={16}/>}
                {signing ? 'กำลังอัปโหลด...' : 'เลือกรูปลายเซ็น (JPG/PNG)'}
              </button>
              <input ref={scanRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
                onChange={e => uploadScannedSignature(e.target.files)}/>
            </>
          )}
        </div>
      )}
    </div>
  )
}
